import { Injectable, inject } from '@angular/core';
import { catchError, map, mergeMap } from 'rxjs';
import { of } from 'rxjs';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as AdminUsersActions from './admin-users.actions';
import { AdminUsersService } from '../admin-users.service';

@Injectable()
export class AdminUsersEffects {
  private actions$ = inject(Actions);
  private adminUsersService = inject(AdminUsersService);

  loadUsers$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.loadUsers),
      mergeMap(() =>
        this.adminUsersService.getUsers().pipe(
          map(users => AdminUsersActions.loadUsersSuccess({ users })),
          catchError(error =>
            of(
              AdminUsersActions.loadUsersFailure({
                error: error.message || 'Failed to load users',
              }),
            ),
          ),
        ),
      ),
    );
  });

  updateRoles$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.updateRoles),
      mergeMap(({ userId, roles }) =>
        this.adminUsersService.updateUserRoles(userId, roles).pipe(
          map(response =>
            AdminUsersActions.updateRolesSuccess({
              userId: response.userId,
              roles: response.roles,
            }),
          ),
          catchError(error =>
            of(
              AdminUsersActions.updateRolesFailure({
                error: error.message || 'Failed to update roles',
              }),
            ),
          ),
        ),
      ),
    );
  });

  // API Key effects (SEOMS-349)
  loadApiKeys$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.loadApiKeys),
      mergeMap(() =>
        this.adminUsersService.listApiKeys().pipe(
          map(apiKeys => AdminUsersActions.loadApiKeysSuccess({ apiKeys })),
          catchError(error =>
            of(
              AdminUsersActions.loadApiKeysFailure({
                error: error.message || 'Failed to load API keys',
              }),
            ),
          ),
        ),
      ),
    );
  });

  createApiKey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.createApiKey),
      mergeMap(({ name, userId }) =>
        this.adminUsersService.createApiKey({ name, userId }).pipe(
          map(response => AdminUsersActions.createApiKeySuccess({ response })),
          catchError(error =>
            of(
              AdminUsersActions.createApiKeyFailure({
                error: error.message || 'Failed to create API key',
              }),
            ),
          ),
        ),
      ),
    );
  });

  revokeApiKey$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.revokeApiKey),
      mergeMap(({ keyId }) =>
        this.adminUsersService.revokeApiKey(keyId).pipe(
          map(() => AdminUsersActions.revokeApiKeySuccess({ keyId })),
          catchError(error =>
            of(
              AdminUsersActions.revokeApiKeyFailure({
                error: error.message || 'Failed to revoke API key',
              }),
            ),
          ),
        ),
      ),
    );
  });

  revokeUserTokens$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.revokeUserTokens),
      mergeMap(({ userId }) =>
        this.adminUsersService.revokeUserTokens(userId).pipe(
          map(response =>
            AdminUsersActions.revokeUserTokensSuccess({
              userId,
              revokedCount: response.revokedCount,
            }),
          ),
          catchError(error =>
            of(
              AdminUsersActions.revokeUserTokensFailure({
                error: error.message || 'Failed to revoke tokens',
              }),
            ),
          ),
        ),
      ),
    );
  });

  loadUserTokens$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AdminUsersActions.loadUserTokens),
      mergeMap(({ userId }) =>
        this.adminUsersService.getUserTokens(userId).pipe(
          map(response =>
            AdminUsersActions.loadUserTokensSuccess({
              userId,
              tokens: response.tokens,
              activeCount: response.activeCount,
            }),
          ),
          catchError(error =>
            of(
              AdminUsersActions.loadUserTokensFailure({
                error: error.message || 'Failed to load user tokens',
              }),
            ),
          ),
        ),
      ),
    );
  });
}
